import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface BackgroundSettings {
  image_url: string | null;
  background_color: string | null;
  overlay_color: string | null;
  background_size: 'cover' | 'contain';
  background_position: 'top' | 'center' | 'bottom';
  opacity: number;
}

interface SectionBackgroundProps {
  children: React.ReactNode;
  page: string;
  section: string;
  className?: string;
}

export default function SectionBackground({
  children,
  page,
  section,
  className = '',
}: SectionBackgroundProps) {
  const [settings, setSettings] = useState<BackgroundSettings>({
    image_url: null,
    background_color: null,
    overlay_color: null,
    background_size: 'cover',
    background_position: 'center',
    opacity: 0.15,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBackground = async () => {
      try {
        const { data, error } = await supabase
          .from('backgrounds')
          .select('*')
          .eq('page', page)
          .eq('section', section)
          .maybeSingle();

        if (error) {
          console.error('Error fetching background:', error);
          setError('Failed to load background settings');
          return;
        }

        if (data) {
          setSettings({
            image_url: data.image_url,
            background_color: data.background_color || null,
            overlay_color: data.overlay_color || null,
            background_size: data.background_size || 'cover',
            background_position: data.background_position || 'center',
            opacity: data.opacity ?? 0.15,
          });
        }
      } catch (err) {
        console.error('Error in background fetch:', err);
        setError('Failed to load background settings');
      }
    };

    fetchBackground();
  }, [page, section]);

  if (error) {
    // Return children without background styling if there's an error
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Background layer */}
      {(settings.image_url || settings.background_color) && (
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundColor: settings.background_color || undefined,
            backgroundImage: settings.image_url ? `url(${settings.image_url})` : undefined,
            backgroundSize: settings.background_size,
            backgroundPosition: settings.background_position,
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}

      {/* Overlay layer */}
      {settings.overlay_color && (
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundColor: settings.overlay_color,
            opacity: settings.opacity,
          }}
        />
      )}

      {/* Content layer */}
      <div className="relative z-1">{children}</div>
    </div>
  );
}